import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { reviews as staticReviews } from "@/data/reviews";
import { api, API_BASE } from "@/api/client";
import { Star, Quote } from "lucide-react";
import { motion } from "framer-motion";

export default function Reviews() {
  const { t } = useLang();
  const [dynamic, setDynamic] = useState([]);

  useEffect(() => {
    api.get("/public-reviews").then((r) => setDynamic(r.data || [])).catch(() => setDynamic([]));
  }, []);

  // dynamic first (newest) then static
  const all = [...dynamic, ...staticReviews];

  return (
    <section id="reviews" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-primary font-bold">03 / Reviews</div>
            <h2 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              {t.reviews.title}
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">{t.reviews.subtitle}</p>
          </div>
          <a
            href="https://www.thumbtack.com/profile/services/583110549352751105/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/10 px-4 py-2 text-sm text-primary font-semibold hover:bg-primary hover:text-primary-foreground transition-colors"
            data-testid="thumbtack-link"
          >
            View on Thumbtack →
          </a>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {all.map((r, i) => (
            <motion.div
              key={(r.id || "static") + "-" + i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}
              className="relative rounded-xl border border-border bg-card p-6 hover:border-primary/50 transition-colors"
              data-testid={`review-card-${i}`}
            >
              <Quote className="absolute top-4 right-4 h-6 w-6 text-primary/30" />
              <div className="flex items-center gap-1">
                {Array.from({ length: r.rating || 5 }).map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                ))}
              </div>
              <p className="mt-4 text-sm text-foreground/90 leading-relaxed line-clamp-6">{r.text}</p>
              <div className="mt-5 pt-4 border-t border-border">
                <div className="font-semibold">{r.name}</div>
                <div className="text-xs text-muted-foreground">
                  {r.service}{r.amount ? ` · ${r.amount}` : ""}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
